// frontend/src/features/home/hooks/useHomeMeasures.js

import { useEffect, useState } from "react";
import api from "../../../services/api";
import {
  saveWeightMeasurement,
  saveBodyMeasurement,
} from "../services/homeService";
import { isoAtNoonUTC, todayISO } from "../utils/homeFormatters";

const MEASURES_LAST_KEY = "mariam:measures:lastDate:v1";

const emptyMeasuresForm = () => ({
  left_arm_cm: "",
  right_arm_cm: "",
  waist_cm: "",
  hip_cm: "",
  chest_cm: "",
  left_thigh_cm: "",
  right_thigh_cm: "",
  left_calf_cm: "",
  right_calf_cm: "",
  notes: "",
});

export function useHomeMeasures({ sex }) {
  const [lastMeasuresDate, setLastMeasuresDate] = useState("");
  const [latestWeightKg, setLatestWeightKg] = useState(null);

  const [measuresModalOpen, setMeasuresModalOpen] = useState(false);
  const [measuresSaving, setMeasuresSaving] = useState(false);
  const [measuresError, setMeasuresError] = useState("");
  const [measuresSuccess, setMeasuresSuccess] = useState("");

  const [entryDate, setEntryDate] = useState(todayISO());
  const [weightKg, setWeightKg] = useState("");
  const [weightNotes, setWeightNotes] = useState("");
  const [measuresForm, setMeasuresForm] = useState(emptyMeasuresForm());

  const fetchLatestMeasures = async () => {
    try {
      const [wRes, bRes] = await Promise.all([
        api.get("/measurements/weight/latest").catch(() => null),
        api.get("/measurements/body/latest").catch(() => null),
      ]);

      const wItem = wRes?.data?.latest || wRes?.data?.item || wRes?.data || null;
      const bItem = bRes?.data?.latest || bRes?.data?.item || bRes?.data || null;

      const wDate = wItem?.loggedAt || wItem?.logged_at || wItem?.created_at || wItem?.date;
      const bDate = bItem?.measuredAt || bItem?.measured_at || bItem?.created_at || bItem?.date;

      const wKg = wItem?.weightKg ?? wItem?.weight_kg ?? wItem?.weight;
      if (wKg !== undefined && wKg !== null && !Number.isNaN(Number(wKg))) {
        setLatestWeightKg(Number(wKg));
      }

      const timestamps = [];
      if (wDate) timestamps.push({ time: new Date(wDate).getTime(), iso: String(wDate) });
      if (bDate) timestamps.push({ time: new Date(bDate).getTime(), iso: String(bDate) });

      if (timestamps.length > 0) {
        timestamps.sort((a, b) => b.time - a.time);
        const latestIso = timestamps[0].iso.split("T")[0];
        setLastMeasuresDate(latestIso);
        try { localStorage.setItem(MEASURES_LAST_KEY, latestIso); } catch {}
        return;
      }
    } catch {
      // fallback local storage
    }

    setLastMeasuresDate(localStorage.getItem(MEASURES_LAST_KEY) || "");
  };

  useEffect(() => {
    fetchLatestMeasures();
  }, []);

  const openMeasuresModal = () => {
    setMeasuresError("");
    setMeasuresSuccess("");
    setMeasuresSaving(false);
    setEntryDate(todayISO());
    setWeightKg("");
    setWeightNotes("");
    setMeasuresForm(emptyMeasuresForm());
    setMeasuresModalOpen(true);
  };

  const closeMeasuresModal = () => {
    if (measuresSaving) return;

    setMeasuresModalOpen(false);
    setMeasuresError("");
    setMeasuresSuccess("");
  };

  const setField = (name, value) => {
    setMeasuresForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toNumberOrNull = (value) => {
    if (value === "" || value === null || value === undefined) {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const hasAnyMeasure = (payload) => {
    const keys = [
      "left_arm_cm",
      "right_arm_cm",
      "waist_cm",
      "hip_cm",
      "chest_cm",
      "left_thigh_cm",
      "right_thigh_cm",
      "left_calf_cm",
      "right_calf_cm",
      "leftArmCm",
      "rightArmCm",
      "waistCm",
      "hipCm",
      "chestCm",
      "leftThighCm",
      "rightThighCm",
      "leftCalfCm",
      "rightCalfCm",
    ];

    return keys.some((key) => payload[key] !== null && payload[key] !== undefined);
  };

  const persistLastMeasuresDate = (value) => {
    if (!value) return;
    setLastMeasuresDate(value);

    try {
      localStorage.setItem(MEASURES_LAST_KEY, value);
    } catch {
      // sem ação
    }
  };

  const handleSaveMeasures = async ({ saveBody } = { saveBody: false }) => {
    setMeasuresError("");
    setMeasuresSuccess("");

    const dateOnly =
      entryDate && /^\d{4}-\d{2}-\d{2}$/.test(entryDate) ? entryDate : todayISO();

    const isoNoon = isoAtNoonUTC(dateOnly);
    const isoNow = new Date().toISOString();
    const measuredISO = isoNoon || isoNow;

    const parsedWeight = toNumberOrNull(weightKg);
    const shouldSaveWeight = parsedWeight !== null && parsedWeight > 0;

    const bodyPayload = {
      measured_at: measuredISO,
      left_arm_cm: toNumberOrNull(measuresForm.left_arm_cm),
      right_arm_cm: toNumberOrNull(measuresForm.right_arm_cm),
      waist_cm: toNumberOrNull(measuresForm.waist_cm),
      hip_cm: toNumberOrNull(measuresForm.hip_cm),
      chest_cm: toNumberOrNull(measuresForm.chest_cm),
      left_thigh_cm: toNumberOrNull(measuresForm.left_thigh_cm),
      right_thigh_cm: toNumberOrNull(measuresForm.right_thigh_cm),
      left_calf_cm: toNumberOrNull(measuresForm.left_calf_cm),
      right_calf_cm: toNumberOrNull(measuresForm.right_calf_cm),
      notes: (measuresForm.notes || "").trim() || null,

      measuredAt: measuredISO,
      date: dateOnly,
      leftArmCm: toNumberOrNull(measuresForm.left_arm_cm),
      rightArmCm: toNumberOrNull(measuresForm.right_arm_cm),
      waistCm: toNumberOrNull(measuresForm.waist_cm),
      hipCm: toNumberOrNull(measuresForm.hip_cm),
      chestCm: toNumberOrNull(measuresForm.chest_cm),
      leftThighCm: toNumberOrNull(measuresForm.left_thigh_cm),
      rightThighCm: toNumberOrNull(measuresForm.right_thigh_cm),
      leftCalfCm: toNumberOrNull(measuresForm.left_calf_cm),
      rightCalfCm: toNumberOrNull(measuresForm.right_calf_cm),
    };

    if (sex === "female") {
      bodyPayload.chest_cm = null;
      bodyPayload.chestCm = null;
    }

    if (sex === "male") {
      bodyPayload.hip_cm = null;
      bodyPayload.hipCm = null;
    }

    const shouldSaveBody = Boolean(saveBody) && hasAnyMeasure(bodyPayload);

    if (!shouldSaveWeight && !shouldSaveBody) {
      setMeasuresError("Informe o peso e/ou abra e preencha alguma medida corporal.");
      return;
    }

    setMeasuresSaving(true);

    try {
      let finalLastDate = "";

      if (shouldSaveWeight) {
        await saveWeightMeasurement({
          date: dateOnly,
          weightKg: parsedWeight,
          notes: (weightNotes || "").trim() || undefined,
        });

        finalLastDate = dateOnly;
      }

      if (shouldSaveBody) {
        const response = await saveBodyMeasurement(bodyPayload);

        const measuredAt =
          response?.measured_at ||
          response?.measuredAt ||
          response?.item?.measuredAt ||
          measuredISO;

        finalLastDate = measuredAt || finalLastDate;
      }

      if (finalLastDate) {
        persistLastMeasuresDate(finalLastDate);
      }

      await fetchLatestMeasures();

      setMeasuresSuccess("Salvo com sucesso ✨");

      window.setTimeout(() => {
        setMeasuresModalOpen(false);
        setMeasuresSuccess("");
      }, 650);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Não consegui salvar agora. Tente novamente em instantes.";

      setMeasuresError(message);
    } finally {
      setMeasuresSaving(false);
    }
  };

  return {
    lastMeasuresDate,
    latestWeightKg,
    measuresModalOpen,
    measuresSaving,
    measuresError,
    measuresSuccess,
    entryDate,
    setEntryDate,
    weightKg,
    setWeightKg,
    weightNotes,
    setWeightNotes,
    measuresForm,
    setField,
    openMeasuresModal,
    closeMeasuresModal,
    handleSaveMeasures,
    fetchLatestMeasures,
  };
}